// ----------------------------------------------------------------------

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/dashboard';

// ----------------------------------------------------------------------

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  // Staff sign-in: a new account must choose its own password before anything else.
  changePassword: path(ROOTS_AUTH, '/change-password'),
};



export const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  general: {
    app: path(ROOTS_DASHBOARD, '/app'),
  },
  packages: path(ROOTS_DASHBOARD, '/packages'),
  promoCode: path(ROOTS_DASHBOARD, '/promo-code'),
  blogs: path(ROOTS_DASHBOARD, '/blog'),
  successStories: path(ROOTS_DASHBOARD, '/success-stories'),
  companyProfileContent: path(ROOTS_DASHBOARD, '/company-profile'),
  events: path(ROOTS_DASHBOARD, '/events'),
  // Program cohorts + their per-batch quiz reports. batchId may be the literal
  // 'none' — the pseudo-batch for attempts carrying no batch.
  // The single quiz-analysis surface: program -> batch -> quiz.
  // Accepts ?eventId= & ?batchId= for deep links from a batch report.
  programQuizAnalysis: path(ROOTS_DASHBOARD, '/events/quiz-analysis'),
  programBatches: (eventId: string) => path(ROOTS_DASHBOARD, `/events/${eventId}/batches`),
  programBatchReport: (eventId: string, batchId: string) =>
    path(ROOTS_DASHBOARD, `/events/${eventId}/batches/${batchId}`),
  seoPages: path(ROOTS_DASHBOARD, '/seoPages'),
  homeStats: path(ROOTS_DASHBOARD, '/home-stats'),
  aboutPage: path(ROOTS_DASHBOARD, '/about-page'),
  notifications: path(ROOTS_DASHBOARD, '/notifications'),
  // EPIC E7: the class-reminder delivery ledger. Deep-linkable with
  // ?diplomaId= &batchName= &sessionId= &userId= &problems=true — the
  // per-session Reminders tab under Batch Management links straight into it.
  reminders: path(ROOTS_DASHBOARD, '/reminders'),

  // PARTNER PLATFORM S1: the application review queue, the partners list and
  // the tabbed partner page (profile / agreements / ledger).
  //
  // `applications` is a STATIC segment under the same root as `detail(:id)`, so
  // its route MUST be declared before the parameterised sibling in
  // routes/index.tsx — otherwise /dashboard/partners/applications resolves as a
  // partner whose id is the literal string "applications".
  //
  // `root` and `list` are the SAME path on purpose — unlike `user`, the section
  // root IS the list. Both names are kept so a caller can say which it means.
  //
  // `detail` takes ?tab=profile|agreements|ledger. The partner page reads the
  // tab from the query string so the Ledger tab can be linked to and sent to
  // somebody, which is exactly what happens when an integrity check fails.
  // PARTNER PLATFORM S2 adds the course review queue and the course review
  // page. `courses` is a THIRD static segment under the same root as
  // `detail(:id)` and falls under exactly the same rule as `applications`:
  // routes/index.tsx MUST declare the literal `courses` segment BEFORE the
  // parameterised `:id` sibling, or /dashboard/partners/courses resolves as a
  // partner whose id is the literal string "courses".
  //
  // `courseDetail` takes ?tab=content|checklist and `courses` takes
  // ?tab=queue|revisions|settings, both read from the query string so a queue
  // filtered to one partner, or a revision diff, can be sent to somebody.
  //
  // `courses` ALSO takes ?partnerId=<id>, which narrows the queue to one
  // partner. It is `courseQueueForPartner()` below, and it exists because the
  // filter was already read, sent and rendered as a removable chip while NOTHING
  // IN THE APP PRODUCED THE LINK — a deep-link design that was right, with no
  // door into it.
  //
  // THE BUNNY EPIC adds `bunny`, a FOURTH static segment under the same root as
  // `detail(:id)`, under exactly the same rule as `applications` and `courses`:
  // routes/index.tsx MUST declare the literal `bunny` segment BEFORE the
  // parameterised `:id` sibling, or /dashboard/partners/bunny resolves as a
  // partner whose id is the literal string "bunny" and answers the service's own
  // "Not found." — which is indistinguishable from a partner that does not
  // exist.
  partners: {
    root: path(ROOTS_DASHBOARD, '/partners'),
    applications: path(ROOTS_DASHBOARD, '/partners/applications'),
    courses: path(ROOTS_DASHBOARD, '/partners/courses'),
    bunny: path(ROOTS_DASHBOARD, '/partners/bunny'),
    list: path(ROOTS_DASHBOARD, '/partners'),
    detail: (id: string) => path(ROOTS_DASHBOARD, `/partners/${id}`),
    courseDetail: (id: string) => path(ROOTS_DASHBOARD, `/partners/courses/${id}`),
    /** The course queue, narrowed to one partner. `?partnerId=` is read by the
     *  queue page on first paint and seeds its filter. */
    courseQueueForPartner: (partnerId: string) =>
      `${path(ROOTS_DASHBOARD, '/partners/courses')}?partnerId=${encodeURIComponent(partnerId)}`,
  },

  instructors: path(ROOTS_DASHBOARD, '/instructors'),
  instructorDetail: (id: string) => path(ROOTS_DASHBOARD, `/instructors/${id}`),

  messages: path(ROOTS_DASHBOARD, '/messages'),
  categories: path(ROOTS_DASHBOARD, '/category'),
  diplomas: path(ROOTS_DASHBOARD, '/diplomas'),
  // Staff (admin/customer-care/super-admin) landing that lists all batches.
  batchManagement: path(ROOTS_DASHBOARD, '/batch-management'),
  // Batch Management: reached from a diploma's report/batches table. batchName
  // may contain spaces, so callers must encodeURIComponent it.
  diplomaBatch: (id: string, batchName: string) =>
    path(ROOTS_DASHBOARD, `/diplomas/${id}/batches/${encodeURIComponent(batchName)}`),
  // Landing page editor for one diploma, and the list of all landing pages.
  diplomaLanding: (id: string) => path(ROOTS_DASHBOARD, `/diplomas/${id}/landing`),
  landingPages: path(ROOTS_DASHBOARD, '/landing-pages'),
  // Tracked lead forms (lead-forms SPEC §D.1): the builder list, one form's
  // editor (?tab=), and the leads inbox (?form=<id> filters it to one form).
  leadForms: path(ROOTS_DASHBOARD, '/lead-forms'),
  // SPEC-S.8.2: the live Google Sheet settings (a static path: it outranks lead-forms/:id).
  leadSheet: path(ROOTS_DASHBOARD, '/lead-forms/google-sheet'),
  leadForm: (id: string) => path(ROOTS_DASHBOARD, `/lead-forms/${id}`),
  // SPEC-T.8.4: where the Google sign-in lands (the popup posts the result to the opener tab and closes).
  googleConnected: path(ROOTS_DASHBOARD, '/google-connected'),
  leads: path(ROOTS_DASHBOARD, '/leads'),
  tracks: path(ROOTS_DASHBOARD, '/tracks'),
  courses: {
    root: path(ROOTS_DASHBOARD, '/courses'),
    view: (id: string) => path(ROOTS_DASHBOARD, `/courses/${id}`),
  },
  screens:path(ROOTS_DASHBOARD, '/screens'),
  certificates: path(ROOTS_DASHBOARD, '/certificates'),
  payments: path(ROOTS_DASHBOARD, '/payments'),
  quizResults: path(ROOTS_DASHBOARD, '/quiz-results'),
  studentAlerts: path(ROOTS_DASHBOARD, '/student-alerts'),
  instructorRatings: path(ROOTS_DASHBOARD, '/instructor-ratings'),
  finalProjects: path(ROOTS_DASHBOARD, '/final-projects'),
  userSegments: path(ROOTS_DASHBOARD, '/user-segments'),
  reports: path(ROOTS_DASHBOARD, '/reports'),
  user: {
    root: path(ROOTS_DASHBOARD, '/user'),
    list: path(ROOTS_DASHBOARD, '/user/list'),

    account: path(ROOTS_DASHBOARD, '/user/account'),
  },
  // Phase 2.4: admin self-service.
  account: {
    root: path(ROOTS_DASHBOARD, '/account'),
    security: path(ROOTS_DASHBOARD, '/account/security'),
    sessions: path(ROOTS_DASHBOARD, '/account/sessions'),
  },
  // Phase 3.5: super-admin only management.
  settings: {
    root: path(ROOTS_DASHBOARD, '/settings'),
    admins: path(ROOTS_DASHBOARD, '/settings/admins'),
    adminSessions: (id: string) =>
      path(ROOTS_DASHBOARD, `/settings/admins/${id}/sessions`),
    // Phase 7.10: admin-action audit log.
    audit: path(ROOTS_DASHBOARD, '/settings/audit'),
    // E1: Zoom accounts & host pool (super-admin only).
    zoom: path(ROOTS_DASHBOARD, '/settings/zoom'),
    // E6: the recording pipeline's standing health report. Read-only, and
    // deliberately wider than the rest of this group — an admin who publishes
    // and retries recordings all day needs the page that says why they are
    // failing, and no credential is entered on it.
    recordings: path(ROOTS_DASHBOARD, '/settings/recordings'),
  },
  // Phase 6.8: customer-care impersonation surface (support + super-admin).
  support: {
    root: path(ROOTS_DASHBOARD, '/support'),
    impersonate: path(ROOTS_DASHBOARD, '/support/impersonate'),
    impersonationsActive: path(ROOTS_DASHBOARD, '/support/active'),
  },
};

export const PATH_DOCS = 'https://docs-minimals.vercel.app/introduction';
